import {
  useState,
  useEffect,
  useCallback,
  lazy,
  Suspense,
  useRef,
} from "react";
import { captureException } from "@/lib/sentry";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { fbTrack, fbTrackCustom } from "@/lib/pixel";
const Login = lazy(() =>
  import("@/components/auth/Login").then((m) => ({
    default: m.Login,
  })),
);
const LocalSaveBanner = lazy(() =>
  import("@/components/LocalSaveBanner").then((m) => ({
    default: m.LocalSaveBanner,
  })),
);
const AlbumList = lazy(() =>
  import("@/components/albums/AlbumList").then((m) => ({
    default: m.AlbumList,
  })),
);
const AlbumDetail = lazy(() =>
  import("@/components/albums/AlbumDetail").then((m) => ({
    default: m.AlbumDetail,
  })),
);
const TradingView = lazy(() =>
  import("@/components/trading/TradingView").then((m) => ({
    default: m.TradingView,
  })),
);
const GlobalAlbumManager = lazy(() =>
  import("@/components/albums/GlobalAlbumManager").then((m) => ({
    default: m.GlobalAlbumManager,
  })),
);
const AdminPanel = lazy(() =>
  import("@/components/admin/AdminPanel").then((m) => ({
    default: m.AdminPanel,
  })),
);
const MarketplaceView = lazy(() =>
  import("@/components/trading/MarketplaceView").then((m) => ({
    default: m.MarketplaceView,
  })),
);
const QrCompareView = lazy(() =>
  import("@/components/albums/QrCompareView").then((m) => ({
    default: m.QrCompareView,
  })),
);
const GroupSessionView = lazy(() =>
  import("@/components/albums/GroupSessionView").then((m) => ({
    default: m.GroupSessionView,
  })),
);
const FlowReturnHandler = lazy(() =>
  import("@/components/premium/FlowReturnHandler").then((m) => ({
    default: m.FlowReturnHandler,
  })),
);
const FeedbackButton = lazy(() =>
  import("@/components/FeedbackButton").then((m) => ({
    default: m.FeedbackButton,
  })),
);
import { Toaster } from "@/components/ui/sonner";
import { Album, GlobalAlbumDefinition } from "@/types";
import { toast } from "sonner";
import { resolveQrParam, QrTradePayload } from "@/lib/qr-trade";
import { useLocalCollection } from "@/hooks/use-local-collection";
import { useUpdatePrompt } from "@/hooks/use-update-prompt";
import {
  LockKeyIcon,
  SignInIcon,
  ArrowLeftIcon as ArrowLeftNavIcon,
} from "@phosphor-icons/react";

type View =
  | "catalog"
  | "list"
  | "detail"
  | "trading"
  | "admin"
  | "marketplace"
  | "qr-compare"
  | "group-session";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapGlobalAlbumDef(dbAlbum: any): GlobalAlbumDefinition {
  return {
    id: dbAlbum.id,
    name: dbAlbum.name,
    description: dbAlbum.description || "",
    totalStickers: dbAlbum.total_stickers,
    year: dbAlbum.year || undefined,
    category: dbAlbum.category || undefined,
    imageUrl: dbAlbum.image_url || undefined,
    sections: dbAlbum.album_sections || [],
    numberingStart: dbAlbum.numbering_start ?? 1,
    approved: dbAlbum.is_approved || false,
    proposedBy: dbAlbum.created_by || undefined,
    createdAt: new Date(dbAlbum.created_at).getTime(),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildStickerRecord(
  userStickers: any[],
): Record<number, { number: number; owned: boolean; duplicates: number }> {
  const record: Record<
    number,
    { number: number; owned: boolean; duplicates: number }
  > = {};
  for (const sticker of userStickers) {
    record[sticker.sticker_number] = {
      number: sticker.sticker_number,
      owned: sticker.quantity > 0,
      duplicates: Math.max(0, sticker.quantity - 1),
    };
  }
  return record;
}

function App() {
  const { user, isLoading } = useAuth();
  useUpdatePrompt();
  const prevUserRef = useRef<typeof user | undefined>(undefined);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [globalAlbums, setGlobalAlbums] = useState<GlobalAlbumDefinition[]>([]);
  const [currentView, setCurrentView] = useState<View>("list");
  const [currentAlbumId, setCurrentAlbumId] = useState<string | null>(null);
  const [qrPayload, setQrPayload] = useState<QrTradePayload | null>(null);
  const [groupSessionId, setGroupSessionId] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [tradingInitialTab, setTradingInitialTab] = useState("points");
  const [isLoadingAlbums, setIsLoadingAlbums] = useState(true);
  const [isCreatingAlbum, setIsCreatingAlbum] = useState(false);
  const localCollection = useLocalCollection();

  // True when the user is using the app without an account
  const isAnonymous = !user;

  const refreshGlobalAlbums = useCallback(async () => {
    // Fetch all albums (not just approved) so pending ones still display their names
    const { data, error } = await supabase.from("global_albums").select("*");
    if (error) {
      // "Failed to fetch" = Chrome/Firefox; "Load failed" = iOS/WebKit — both are offline/network errors
      if (
        !error.message?.includes("Failed to fetch") &&
        !error.message?.includes("Load failed")
      ) {
        captureException(new Error(error.message), {
          extra: {
            action: "fetch_global_albums",
            code: error.code,
            details: error.details,
            hint: error.hint,
          },
        });
      }
      return;
    }
    if (data) setGlobalAlbums(data.map(mapGlobalAlbumDef));
  }, []);

  // Dispara Lead en Meta Pixel la primera vez que el usuario inicia sesión
  useEffect(() => {
    if (isLoading) return;
    if (prevUserRef.current === undefined) {
      // primera evaluación tras carga: solo guardar estado inicial
      prevUserRef.current = user;
      return;
    }
    if (!prevUserRef.current && user) {
      fbTrack("Lead");
    }
    prevUserRef.current = user;
  }, [user, isLoading]);

  const QR_SESSION_KEY = "pending_qr_trade";
  const GROUP_SESSION_KEY = "pending_group_session";

  // Detect ?qr= URL param on load.
  // If the user is already logged in → activate compare view immediately.
  // If not logged in → persist in sessionStorage so it survives the login flow.
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const qr = params.get("qr");
    const gs = params.get("gs");

    if (qr || gs) {
      // Clean the URL immediately so reloads don't re-trigger
      globalThis.history.replaceState({}, "", globalThis.location.pathname);
    }

    if (qr) {
      if (user) {
        resolveQrParam(qr).then((payload) => {
          if (payload) {
            setQrPayload(payload);
            setCurrentView("qr-compare");
          }
        });
      } else {
        sessionStorage.setItem(QR_SESSION_KEY, qr);
      }
    }

    // Group session links work for everyone (anonymous join allowed)
    if (gs) {
      setGroupSessionId(gs);
      setCurrentView("group-session");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After login, check for a pending QR payload saved before authentication.
  useEffect(() => {
    if (!user) return;
    const stored = sessionStorage.getItem(QR_SESSION_KEY);
    if (stored) {
      sessionStorage.removeItem(QR_SESSION_KEY);
      resolveQrParam(stored).then((payload) => {
        if (payload) {
          setQrPayload(payload);
          setCurrentView("qr-compare");
          fetchUserAlbums();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Once auth finishes loading and there is no user, pre-load the global catalog
  // so it's ready if the visitor navigates to it.
  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      refreshGlobalAlbums();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const fetchUserAlbums = useCallback(async () => {
    if (!user?.id) return;
    const { data: userAlbumsData, error: userAlbumsError } = await supabase
      .from("user_albums")
      .select(
        `
          id,
          global_album_id,
          created_at,
          user_stickers (
            sticker_number,
            quantity
          )
        `,
      )
      .eq("user_id", user.id);

    if (userAlbumsError) {
      // "Failed to fetch" = Chrome/Firefox; "Load failed" = iOS/WebKit — both are offline/network errors
      if (
        !userAlbumsError.message?.includes("Failed to fetch") &&
        !userAlbumsError.message?.includes("Load failed")
      ) {
        captureException(new Error(userAlbumsError.message), {
          extra: {
            action: "fetch_user_albums",
            code: userAlbumsError.code,
            details: userAlbumsError.details,
            hint: userAlbumsError.hint,
          },
        });
      }
      setIsLoadingAlbums(false);
      return;
    }

    if (userAlbumsData) {
      const mappedUserAlbums: Album[] = userAlbumsData.map((dbUserAlbum) => {
        const stickersRecord = buildStickerRecord(
          dbUserAlbum.user_stickers ?? [],
        );

        return {
          id: dbUserAlbum.id,
          globalAlbumId: dbUserAlbum.global_album_id,
          createdAt: new Date(dbUserAlbum.created_at).getTime(),
          data: {
            albumSize: 0, // Will be updated when merged with global album
            stickers: stickersRecord,
          },
        };
      });
      setAlbums(mappedUserAlbums);
    }
    setIsLoadingAlbums(false);
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;

    const run = async () => {
      setIsLoadingAlbums(true);
      // Migrate any locally-saved data to Supabase first
      if (localCollection.hasLocalData) {
        const ok = await localCollection.migrateToSupabase(user.id);
        if (ok)
          toast.success("¡Tus datos locales fueron guardados en tu cuenta!");
      }
      refreshGlobalAlbums();
      await fetchUserAlbums();
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Update album sizes once global albums are loaded
  useEffect(() => {
    if (albums.length > 0 && globalAlbums.length > 0) {
      let hasChanges = false;
      const updatedAlbums = albums.map((album) => {
        const globalDef = globalAlbums.find(
          (g) => g.id === album.globalAlbumId,
        );

        // Fix: Make sure we check if albumSize needs updating AND isn't just missing data
        // Also ensure data object exists
        const currentSize = album.data?.albumSize || 0;

        if (globalDef && currentSize !== globalDef.totalStickers) {
          hasChanges = true;
          return {
            ...album,
            data: {
              ...album.data,
              albumSize: globalDef.totalStickers,
              stickers: album.data?.stickers || {},
            },
          };
        }
        return album;
      });

      if (hasChanges) {
        setAlbums(updatedAlbums);
      }
    }
  }, [globalAlbums, albums]); // Removed .length to ensure updates happen even on initial load if references change

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Cargando...
      </div>
    );
  }

  // Show login overlay
  if (showLogin) {
    return (
      <>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              Cargando...
            </div>
          }
        >
          <Login onBack={() => setShowLogin(false)} />
        </Suspense>
        <Toaster />
      </>
    );
  }

  // ---- From here: user is authenticated OR is anonymous (local mode) ----

  const currentAlbums = user ? albums : localCollection.albums;
  const currentAlbum = currentAlbums?.find((a) => a.id === currentAlbumId);

  // refreshGlobalAlbums is defined above with useCallback

  const createAlbumFromGlobal = async (
    globalAlbumDef: GlobalAlbumDefinition,
  ) => {
    if (!user) {
      localCollection.createAlbum(globalAlbumDef);
      toast.success(`Álbum "${globalAlbumDef.name}" agregado a tu colección`);
      setCurrentView("list");
      return;
    }

    // Prevent double-tap race conditions on mobile
    if (isCreatingAlbum) return;
    setIsCreatingAlbum(true);

    try {
      let row: { id: string; created_at: string } | null = null;

      const { data, error } = await supabase
        .from("user_albums")
        .upsert(
          { user_id: user.id, global_album_id: globalAlbumDef.id },
          { onConflict: "user_id,global_album_id" },
        )
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          // Race condition: a concurrent request already inserted this row.
          // Fetch the existing record and proceed as if the upsert succeeded.
          const { data: existing } = await supabase
            .from("user_albums")
            .select("id, created_at")
            .eq("user_id", user.id)
            .eq("global_album_id", globalAlbumDef.id)
            .single();
          row = existing;
        }

        if (!row) {
          captureException(new Error(error.message), {
            extra: {
              action: "create_album",
              code: error.code,
              details: error.details,
              hint: error.hint,
            },
          });
          toast.error("Error al crear el álbum");
          return;
        }
      } else {
        row = data;
      }

      const newAlbum: Album = {
        id: row.id,
        globalAlbumId: globalAlbumDef.id,
        createdAt: new Date(row.created_at).getTime(),
        data: {
          albumSize: globalAlbumDef.totalStickers,
          stickers: {},
        },
      };

      // Update state immediately with the new album
      setAlbums((current) => {
        const updated = [...(current || []), newAlbum];
        // Primer álbum: conversión completa
        if (updated.length === 1) {
          fbTrack("CompleteRegistration");
        }
        fbTrackCustom("AlbumAdded", { album_name: globalAlbumDef.name });
        return updated;
      });
      toast.success(`Álbum "${globalAlbumDef.name}" agregado a tu colección`);
      setCurrentView("list");
    } finally {
      setIsCreatingAlbum(false);
    }
  };

  const updateAlbum = (updatedAlbum: Album) => {
    if (!user) {
      localCollection.updateAlbum(updatedAlbum);
      return;
    }
    setAlbums((current) =>
      (current || []).map((album) =>
        album.id === updatedAlbum.id ? updatedAlbum : album,
      ),
    );
  };

  const deleteAlbum = async (albumId: string) => {
    if (!user) {
      const albumToDelete = localCollection.albums.find(
        (a) => a.id === albumId,
      );
      const globalAlbumLocal = albumToDelete
        ? globalAlbums?.find((g) => g.id === albumToDelete.globalAlbumId)
        : null;
      localCollection.deleteAlbum(albumId);
      if (globalAlbumLocal) {
        toast.success(
          `Álbum "${globalAlbumLocal.name}" eliminado de tu colección`,
        );
      }
      return;
    }

    const { error } = await supabase
      .from("user_albums")
      .delete()
      .eq("id", albumId);

    if (error) {
      captureException(new Error(error.message), {
        extra: {
          action: "delete_album",
          code: error.code,
          details: error.details,
          hint: error.hint,
        },
      });
      toast.error("Error al eliminar el álbum");
      return;
    }

    const albumToDelete = albums?.find((a) => a.id === albumId);
    const globalAlbum = albumToDelete
      ? globalAlbums?.find((g) => g.id === albumToDelete.globalAlbumId)
      : null;
    setAlbums((current) =>
      (current || []).filter((album) => album.id !== albumId),
    );

    if (globalAlbum) {
      toast.success(`Álbum "${globalAlbum.name}" eliminado de tu colección`);
    }
  };

  const openAlbum = (albumId: string) => {
    setCurrentAlbumId(albumId);
    setCurrentView("detail");
  };

  const backToList = () => {
    setCurrentView("list");
    setCurrentAlbumId(null);
  };

  const albumsWithMetadata =
    currentAlbums?.map((album) => {
      const globalAlbum = globalAlbums?.find(
        (g) => g.id === album.globalAlbumId,
      );
      return { ...album, globalAlbum };
    }) || [];

  // Update currentGlobalAlbum dynamically using the latest globalAlbums state
  const currentGlobalAlbum = currentAlbum
    ? globalAlbums?.find((g) => g.id === currentAlbum.globalAlbumId)
    : null;

  // If we are looking at an album, merge the true size before passing it down
  const activeAlbum =
    currentAlbum && currentGlobalAlbum
      ? {
          ...currentAlbum,
          data: {
            ...currentAlbum.data,
            albumSize: currentGlobalAlbum.totalStickers,
          },
        }
      : currentAlbum;

  return (
    <>
      <main>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              Cargando...
            </div>
          }
        >
          {currentView === "catalog" && (
            <GlobalAlbumManager
              onSelectAlbum={createAlbumFromGlobal}
              isAddingAlbum={isCreatingAlbum}
              onBack={() => {
                refreshGlobalAlbums();
                backToList();
              }}
            />
          )}

          {currentView === "list" && (
            <AlbumList
              albums={albumsWithMetadata}
              globalAlbums={globalAlbums || []}
              onOpenCatalog={() => setCurrentView("catalog")}
              onOpenAlbum={openAlbum}
              onDeleteAlbum={deleteAlbum}
              onOpenTrading={(tab) => {
                setTradingInitialTab(tab ?? "points");
                setCurrentView("trading");
              }}
              onOpenAdmin={() => setCurrentView("admin")}
              onOpenMarketplace={() => setCurrentView("marketplace")}
              onLogin={() => setShowLogin(true)}
            />
          )}

          {currentView === "detail" && activeAlbum && currentGlobalAlbum && (
            <AlbumDetail
              album={activeAlbum}
              globalAlbum={currentGlobalAlbum}
              onUpdateAlbum={updateAlbum}
              onBack={backToList}
              isAnonymous={!user}
              onNavigateToGroupSession={(id) => {
                setGroupSessionId(id);
                setCurrentView("group-session");
              }}
            />
          )}

          {currentView === "trading" && !user && (
            <div className="min-h-screen flex flex-col items-center justify-center gap-5 p-8 text-center bg-background">
              <LockKeyIcon
                className="h-14 w-14 text-muted-foreground opacity-30"
                weight="duotone"
              />
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">
                  Los intercambios requieren una cuenta
                </h2>
                <p className="text-muted-foreground max-w-sm text-sm">
                  Para encontrar coleccionistas cerca de ti, necesitamos saber
                  tu país. Eso lo configuras tú en tu perfil.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogin(true)}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  <SignInIcon className="h-4 w-4" />
                  Crear cuenta o iniciar sesión
                </button>
                <button
                  type="button"
                  onClick={backToList}
                  className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
                >
                  <ArrowLeftNavIcon className="h-4 w-4" />
                  Volver
                </button>
              </div>
            </div>
          )}

          {currentView === "trading" && user && (
            <TradingView
              albums={albumsWithMetadata}
              globalAlbums={globalAlbums || []}
              onBack={backToList}
              initialTab={tradingInitialTab}
            />
          )}

          {currentView === "admin" && (
            <AdminPanel
              onBack={() => {
                refreshGlobalAlbums();
                backToList();
              }}
            />
          )}

          {currentView === "marketplace" && !user && (
            <div className="min-h-screen flex flex-col items-center justify-center gap-5 p-8 text-center bg-background">
              <LockKeyIcon
                className="h-14 w-14 text-muted-foreground opacity-30"
                weight="duotone"
              />
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">
                  El marketplace requiere una cuenta
                </h2>
                <p className="text-muted-foreground max-w-sm text-sm">
                  Creá una cuenta para publicar y ver ofertas de otros
                  coleccionistas.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogin(true)}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  <SignInIcon className="h-4 w-4" />
                  Crear cuenta o iniciar sesión
                </button>
                <button
                  type="button"
                  onClick={backToList}
                  className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
                >
                  <ArrowLeftNavIcon className="h-4 w-4" />
                  Volver
                </button>
              </div>
            </div>
          )}

          {currentView === "marketplace" && user && (
            <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/10">
              <div className="container mx-auto px-4 py-8 max-w-4xl">
                <button
                  onClick={backToList}
                  className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Volver a Álbumes
                </button>
                <MarketplaceView globalAlbums={globalAlbums || []} />
              </div>
            </div>
          )}

          {currentView === "qr-compare" && qrPayload && (
            <QrCompareView
              scanned={qrPayload}
              myAlbum={
                currentAlbums?.find(
                  (a) => a.globalAlbumId === qrPayload.globalAlbumId,
                ) ?? null
              }
              globalAlbum={
                globalAlbums?.find((g) => g.id === qrPayload.globalAlbumId) ??
                null
              }
              isLoadingAlbums={isLoadingAlbums}
              onBack={() => {
                setCurrentView("list");
                setQrPayload(null);
              }}
              onApplyTrade={async (toGive, toReceive) => {
                const myAlbum = currentAlbums?.find(
                  (a) => a.globalAlbumId === qrPayload.globalAlbumId,
                );
                if (!myAlbum) return;

                const previousStickers = { ...myAlbum.data.stickers };
                const newStickers = { ...previousStickers };

                for (const n of toReceive) {
                  if (newStickers[n]?.owned) {
                    newStickers[n] = {
                      ...newStickers[n],
                      duplicates: newStickers[n].duplicates + 1,
                    };
                  } else {
                    newStickers[n] = { number: n, owned: true, duplicates: 0 };
                  }
                }

                for (const n of toGive) {
                  if (newStickers[n]?.owned && newStickers[n].duplicates > 0) {
                    newStickers[n] = {
                      ...newStickers[n],
                      duplicates: newStickers[n].duplicates - 1,
                    };
                  }
                }

                const updatedAlbum = {
                  ...myAlbum,
                  data: { ...myAlbum.data, stickers: newStickers },
                };
                updateAlbum(updatedAlbum);

                if (user) {
                  const allChanges = [
                    ...toReceive.map((n) => {
                      const existing = previousStickers[n];
                      return {
                        user_album_id: myAlbum.id,
                        sticker_number: n,
                        quantity: existing?.owned ? existing.duplicates + 2 : 1,
                      };
                    }),
                    ...toGive.map((n) => {
                      const existing = previousStickers[n];
                      return {
                        user_album_id: myAlbum.id,
                        sticker_number: n,
                        quantity: existing?.duplicates ?? 1,
                      };
                    }),
                  ];

                  if (allChanges.length > 0) {
                    const { error } = await supabase
                      .from("user_stickers")
                      .upsert(allChanges, {
                        onConflict: "user_album_id,sticker_number",
                      });

                    if (error) {
                      updateAlbum(myAlbum);
                      captureException(new Error(error.message), {
                        extra: {
                          action: "apply_qr_trade",
                          userAlbumId: myAlbum.id,
                          code: error.code,
                        },
                      });
                      toast.error("Error al guardar el intercambio");
                    } else if (
                      qrPayload.userId &&
                      (toGive.length > 0 || toReceive.length > 0)
                    ) {
                      const globalAlbumName =
                        globalAlbums?.find(
                          (g) => g.id === qrPayload.globalAlbumId,
                        )?.name ?? qrPayload.globalAlbumId;
                      await supabase.from("trade_requests").upsert(
                        {
                          from_user_id: user.id,
                          to_user_id: qrPayload.userId,
                          global_album_id: qrPayload.globalAlbumId,
                          album_name: globalAlbumName,
                          from_stickers: toGive,
                          to_stickers: toReceive,
                          common_points: [],
                          status: "pending",
                        },
                        {
                          onConflict: "from_user_id,to_user_id,global_album_id",
                        },
                      );
                    }
                  }
                }
              }}
            />
          )}

          {currentView === "group-session" && groupSessionId && (
            <GroupSessionView
              sessionId={groupSessionId}
              albums={currentAlbums ?? []}
              globalAlbums={globalAlbums ?? []}
              isLoadingAlbums={isLoadingAlbums}
              onBack={() => {
                setCurrentView("list");
                setGroupSessionId(null);
              }}
              onApplied={fetchUserAlbums}
              onRefresh={fetchUserAlbums}
            />
          )}
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <FlowReturnHandler onUnlockConfirmed={refreshGlobalAlbums} />
      </Suspense>

      {/* Botón flotante de feedback — visible para todos */}
      <Suspense fallback={null}>
        <FeedbackButton className="hidden sm:flex fixed bottom-5 right-5 z-40" />
      </Suspense>

      {/* Banner: warn anon users their data is local-only */}
      {isAnonymous && localCollection.hasLocalData && (
        <Suspense fallback={null}>
          <LocalSaveBanner onLogin={() => setShowLogin(true)} />
        </Suspense>
      )}

      <Toaster />
    </>
  );
}

export default App;
